import React, { useEffect, useRef, useState } from 'react';
import { User, ChatConversation } from '../../types';
import { api } from '../../services/apiService';
import ConversationView from './ConversationView';

interface Props {
  leadId: string;
  currentUser: User;
}

// Hauteur attendue : ce composant est utilisé dans un onglet d'une modale
// (LeadDetailsModal), donc on s'adapte au parent via h-full + flex-col.
const LeadDiscussionTab: React.FC<Props> = ({ leadId, currentUser }) => {
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedForLeadRef = useRef<string | null>(null);

  useEffect(() => {
    // Éviter de re-créer la conv si on a déjà fetché pour ce leadId
    if (fetchedForLeadRef.current === leadId) return;
    fetchedForLeadRef.current = leadId;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) Récupère/crée le thread contextuel
        const { id } = await api.chatGetOrCreateForLead(leadId);
        // 2) Récupère le détail de la conv depuis la liste (pour participants + métadonnées)
        const list = await api.chatListConversations();
        if (cancelled) return;
        const conv = list.find(c => c.id === id);
        if (!conv) {
          setError("Conversation introuvable après création.");
        } else {
          setConversation(conv);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [leadId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-slate-500 dark:text-[#546a96]">
        Chargement du thread de discussion…
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-rose-600 dark:text-rose-300">
        {error || 'Conversation introuvable'}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ConversationView
        conversation={conversation}
        currentUser={currentUser}
      />
    </div>
  );
};

export default LeadDiscussionTab;
